import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';
import { zodPdfDlnRequestDoctypeSchema } from './validations';

const zodUpdateCompanyInvoiceSendingSettingsPayloadSchema = z
  .object({
    accountingReferenceToSubject: z.boolean().optional(),
    addDlnsSinglePdf: z.boolean().optional(),
    addDlnsToInvoice: z.boolean().optional(),
    addInvoiceProtocolToInvoice: z.boolean().optional(),
    dlnDoctype: zodPdfDlnRequestDoctypeSchema.optional(),
    dlnSimplifiedJsonsToInvoice: z.boolean().optional(),
    dlnsMultiFileToInvoice: z.boolean().optional(),
    emailReceivedTimeToSubject: z.boolean().optional(),
    includeDlnAttachments: z.boolean().optional(),
    sendInvoiceExternalXml: z.boolean().optional(),
  })
  .transform((value) =>
    withoutObjectKeysWhereValueIs(value, [null, undefined, '']),
  );

type CompanyInvoiceSendingSettingsPayload = z.infer<
  typeof zodUpdateCompanyInvoiceSendingSettingsPayloadSchema
>;

export type UpdateCompanyInvoiceSendingSettingsArguments = {
  companyId: UUID;
  invoiceSendingSettings: CompanyInvoiceSendingSettingsPayload;
};

/**
 * Updates a company's invoice sending settings.
 *
 * @param companyId - The ID of the company to update.
 * @param invoiceSendingSettings - The invoice sending settings to update.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/set_company_invoice_settings_company__id__invoice_sending_settings_put
 */
// fallow-ignore-next-line unused-export
export const updateCompanyInvoiceSendingSettings = async (
  companyId: UUID,
  invoiceSendingSettings: CompanyInvoiceSendingSettingsPayload,
) => {
  try {
    return await vestigasApi
      .put(ENDPOINT.COMPANY.INVOICE_SETTINGS.UPDATE(companyId), {
        json: invoiceSendingSettings,
        validate: {
          body: zodUpdateCompanyInvoiceSendingSettingsPayloadSchema,
          response: zodSuccessResponseSchema,
        },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error updating company invoice sending settings',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * Custom hook for updating a company's invoice sending settings using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/set_company_invoice_settings_company__id__invoice_sending_settings_put
 */
export function useMutationUpdateCompanyInvoiceSendingSettings(options?: any) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      invoiceSendingSettings,
    }: UpdateCompanyInvoiceSendingSettingsArguments) =>
      updateCompanyInvoiceSendingSettings(companyId, invoiceSendingSettings),
    onSettled(_data, _error, { companyId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getInvoiceSendingSettings(companyId),
      });
    },
    ...options,
  });
}
